// SEO-facing accessory landing categories.
//
// Each entry drives a public `/accessories/:slug` page and its sitemap entry.
// `dbValues` maps to `auctions.accessory_category` values (from
// grading/companies.ts). Some SEO slugs are broader than one DB value and
// group several — e.g. Cleaning Accessories.
//
// `titleKeywords` is a lowercase-substring match against the listing title,
// used as a fallback for legacy rows or "Other" catch-all pages.

import type { AccessoryCategory } from "./grading/companies";

export type AccessoryLanding = {
  slug: string;
  label: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  dbValues: AccessoryCategory[];
  titleKeywords?: string[];
};

const CANONICAL = "https://bidnovaauctions.uk";

export const ACCESSORY_LANDINGS: AccessoryLanding[] = [
  {
    slug: "card-sleeves",
    label: "Card Sleeves",
    h1: "Card Sleeves",
    title: "Card Sleeves UK | Trading Card Sleeves Auctions | BidNova Auctions",
    description:
      "Buy trading card sleeves in the UK on BidNova Auctions. Standard, matte, Perfect Fit and inner sleeves from verified sellers with GBP payments and buyer protection.",
    intro:
      "Protect your Pokémon, One Piece, Yu-Gi-Oh! and Magic singles with standard-size, matte and Perfect Fit sleeves. Every listing on BidNova ships from verified UK sellers with Stripe-secured checkout and full buyer protection.",
    dbValues: ["sleeves", "perfect_fit"],
    titleKeywords: ["sleeve"],
  },
  {
    slug: "penny-sleeves",
    label: "Penny Sleeves",
    h1: "Penny Sleeves",
    title: "Penny Sleeves UK | Soft Card Sleeves Auctions | BidNova Auctions",
    description:
      "Shop penny sleeves and soft card sleeves in the UK on BidNova Auctions. Ideal first-layer protection under top loaders. GBP payments, Stripe checkout, buyer protection.",
    intro:
      "Penny sleeves are the standard first-layer protection for raw cards before a top loader or magnetic holder. Browse live UK auctions and Buy It Now listings from verified sellers.",
    dbValues: ["penny_sleeves"],
    titleKeywords: ["penny sleeve"],
  },
  {
    slug: "top-loaders",
    label: "Top Loaders",
    h1: "Top Loaders",
    title: "Top Loaders UK | 35pt & 55pt Card Toploaders | BidNova Auctions",
    description:
      "Buy card top loaders in the UK on BidNova Auctions — 35pt, 55pt, 75pt, 100pt and thicker. GBP prices, Stripe-secured checkout and verified UK sellers.",
    intro:
      "Rigid PVC top loaders in every common thickness — from 35pt for standard cards to 130pt+ for thick relics and jersey cards. Perfect for shipping, storage and display.",
    dbValues: ["top_loaders", "toploader_bags"],
    titleKeywords: ["top loader", "toploader"],
  },
  {
    slug: "semi-rigid-card-holders",
    label: "Semi-Rigid Card Holders",
    h1: "Semi-Rigid Card Holders",
    title: "Semi-Rigid Card Holders UK | Card Savers | BidNova Auctions",
    description:
      "Semi-rigid card holders and Card Savers for UK collectors on BidNova Auctions. The grading-submission standard for raw cards. GBP payments, verified sellers.",
    intro:
      "Semi-rigid holders (often called Card Savers) are the accepted holder for submitting raw cards to PSA, BGS, CGC and other grading companies. Bendable enough to insert cards without damage, rigid enough to protect them in transit.",
    dbValues: ["card_savers"],
    titleKeywords: ["card saver", "semi-rigid", "semi rigid"],
  },
  {
    slug: "magnetic-holders",
    label: "Magnetic Holders",
    h1: "Magnetic Card Holders",
    title: "Magnetic Card Holders UK | One-Touch Holders | BidNova Auctions",
    description:
      "Shop magnetic card holders and One-Touch cases in the UK on BidNova Auctions. 35pt, 55pt, 75pt, 100pt and thicker. GBP prices with buyer protection.",
    intro:
      "Premium magnetic holders and One-Touch cases for display-quality storage of your best raw cards, autos and hits. Available in every point thickness.",
    dbValues: ["magnetic_holders", "one_touch"],
    titleKeywords: ["magnetic", "one-touch", "one touch"],
  },
  {
    slug: "card-binders",
    label: "Card Binders",
    h1: "Card Binders",
    title: "Card Binders UK | 9-Pocket & 12-Pocket TCG Binders | BidNova Auctions",
    description:
      "Buy trading card binders in the UK on BidNova Auctions. Zipped, side-loading, 9-pocket and 12-pocket binders for Pokémon, MTG and more. GBP prices, verified sellers.",
    intro:
      "Zipped and side-loading binders let you organise your collection safely. Choose from 9-pocket or 12-pocket layouts sized for Pokémon, Magic and other standard TCGs.",
    dbValues: ["binders"],
    titleKeywords: ["binder"],
  },
  {
    slug: "binder-pages",
    label: "Binder Pages",
    h1: "Binder Pages",
    title: "Binder Pages UK | 9-Pocket TCG Refill Pages | BidNova Auctions",
    description:
      "Buy binder pages and refill sheets in the UK on BidNova Auctions. 9-pocket, side-loading and top-loading pages compatible with Ultra Pro, Dragon Shield and more.",
    intro:
      "Refill and expand any collector binder with 9-pocket top-loading and side-loading pages. Compatible with the most common ring-binder formats.",
    dbValues: ["binder_pages"],
    titleKeywords: ["binder page", "binder refill", "pocket page"],
  },
  {
    slug: "deck-boxes",
    label: "Deck Boxes",
    h1: "Deck Boxes",
    title: "TCG Deck Boxes UK | Pokémon & Magic Deck Boxes | BidNova Auctions",
    description:
      "Shop deck boxes for Pokémon, Magic: The Gathering, Yu-Gi-Oh! and One Piece on BidNova Auctions. GBP prices, Stripe-secured checkout, verified UK sellers.",
    intro:
      "Sturdy deck boxes for standard, commander and other TCG deck formats — hold sleeved decks safely for play, travel and storage.",
    dbValues: ["deck_boxes"],
    titleKeywords: ["deck box"],
  },
  {
    slug: "storage-boxes",
    label: "Storage Boxes",
    h1: "Card Storage Boxes",
    title: "Card Storage Boxes UK | Bulk Card Boxes | BidNova Auctions",
    description:
      "Buy card storage boxes in the UK on BidNova Auctions. Long boxes, monster boxes and stackable bulk-card storage. GBP prices, verified sellers.",
    intro:
      "Bulk card storage for the rest of your collection — long boxes, monster boxes and stackable card boxes for thousands of sleeved and unsleeved cards.",
    dbValues: ["storage_boxes"],
    titleKeywords: ["storage box", "monster box", "long box"],
  },
  {
    slug: "playmats",
    label: "Playmats",
    h1: "Playmats",
    title: "TCG Playmats UK | Pokémon, MTG & Yu-Gi-Oh! Mats | BidNova Auctions",
    description:
      "Shop trading card playmats in the UK on BidNova Auctions. Neoprene, stitched-edge and premium art mats for Pokémon, Magic and more. GBP prices.",
    intro:
      "Neoprene and stitched-edge playmats give your sleeved cards a protective surface for play, trading and display.",
    dbValues: ["playmats"],
    titleKeywords: ["playmat", "play mat"],
  },
  {
    slug: "grading-submission-kits",
    label: "Grading Submission Kits",
    h1: "Grading Submission Kits",
    title: "Grading Submission Kits UK | PSA, BGS, CGC Supplies | BidNova Auctions",
    description:
      "Grading submission supplies for UK collectors on BidNova Auctions — Card Savers, submission holders and bundles for PSA, BGS, CGC and SGC submissions.",
    intro:
      "Everything you need to submit raw cards for grading: Card Savers, submission-safe sleeves and prep bundles for PSA, BGS, CGC and SGC.",
    dbValues: ["card_savers", "supplies_bundle"],
    titleKeywords: ["grading kit", "submission", "psa kit", "bgs kit"],
  },
  {
    slug: "display-stands",
    label: "Display Stands",
    h1: "Card Display Stands",
    title: "Card Display Stands UK | Slab & Card Holders | BidNova Auctions",
    description:
      "Shop card display stands for graded slabs and raw cards on BidNova Auctions. Acrylic and easel stands from verified UK sellers with buyer protection.",
    intro:
      "Show off your best pulls and graded slabs with acrylic and easel display stands sized for cards and standard grading slabs.",
    dbValues: ["card_stands", "display_cases"],
    titleKeywords: ["stand", "easel", "display case"],
  },
  {
    slug: "cleaning-accessories",
    label: "Cleaning Accessories",
    h1: "Card Cleaning Accessories",
    title: "Card Cleaning Accessories UK | Slab & Card Care | BidNova Auctions",
    description:
      "Card cleaning supplies for UK collectors on BidNova Auctions — microfibre cloths, slab wipes and safe cleaning kits. GBP prices with buyer protection.",
    intro:
      "Keep raw cards and graded slabs looking their best with microfibre cloths, slab-safe wipes and pre-grading cleaning kits from verified UK sellers.",
    dbValues: ["other"],
    titleKeywords: ["clean", "wipe", "microfibre", "microfiber"],
  },
  {
    slug: "other-accessories",
    label: "Other Accessories",
    h1: "Other Trading Card Accessories",
    title: "Other Card Accessories UK | Dice, Counters, Supplies | BidNova Auctions",
    description:
      "Dice, counters, team bags, supplies bundles and other trading card accessories on BidNova Auctions. GBP prices, Stripe-secured checkout, verified UK sellers.",
    intro:
      "Dice, life counters, team bags, supplies bundles and everything else that helps you play, protect and organise your collection.",
    dbValues: ["dice_counters", "team_bags", "supplies_bundle", "other"],
  },
];

export function findAccessoryLanding(slug: string | undefined): AccessoryLanding | null {
  if (!slug) return null;
  return ACCESSORY_LANDINGS.find((c) => c.slug === slug) ?? null;
}

export function canonicalUrl(slug: string): string {
  return `${CANONICAL}/accessories/${slug}`;
}
